import { useEffect } from 'react';

interface DocumentMeta {
  title: string;
  description?: string;
  /** Absolute path on the canonical origin, e.g. "/pricing". */
  path?: string;
}

const BASE_URL = 'https://cadencez.com';
let baseTitleSnapshot: string | null = null;

/**
 * Per-page <title> + description + canonical URL. The index.html
 * provides the static fallback (what crawlers see before JS runs);
 * this hook keeps the browser tab + Google's rendered title in sync
 * once the user lands on a sub-page.
 *
 * No external dependency — react-helmet-async is overkill when the
 * only thing that varies per page is the title bar and Google's
 * rendered <title>. Social-share previews still come from the
 * static OG/Twitter tags in index.html.
 */
export function useDocumentMeta({ title, description, path }: DocumentMeta) {
  useEffect(() => {
    if (baseTitleSnapshot === null) baseTitleSnapshot = document.title;
    document.title = title;

    if (description) upsertMeta('name', 'description', description);

    const canonical = path ? `${BASE_URL}${path}` : BASE_URL;
    upsertLink('canonical', canonical);

    return () => {
      // Restore base title on unmount so the previous page's title
      // doesn't linger on back-navigation.
      if (baseTitleSnapshot) document.title = baseTitleSnapshot;
    };
  }, [title, description, path]);
}

function upsertMeta(attr: 'name' | 'property', value: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}
